import React, { useState, useEffect, useRef } from 'react';
import { upgradeSubscription, apiGet, apiPost } from '../services/apiService';

const PLANS = [
  {
    key: 'basico',
    label: 'Básico',
    price: 'R$9,90',
    originalPrice: 'R$15,90',
    cases: '10 casos/mês',
    priceId: process.env.REACT_APP_PRICE_BASICO || '',
    popular: false,
  },
  {
    key: 'pro',
    label: 'Pro',
    price: 'R$29,90',
    originalPrice: 'R$49,90',
    cases: '40 casos/mês',
    priceId: process.env.REACT_APP_PRICE_PRO || '',
    popular: true,
  },
  {
    key: 'intensivo',
    label: 'Intensivo',
    price: 'R$59,90',
    originalPrice: 'R$79,90',
    cases: '100 casos/mês',
    priceId: process.env.REACT_APP_PRICE_INTENSIVO || '',
    popular: false,
  },
];

interface UpgradeScreenProps {
  currentPlanKey: string;
  onBack: () => void;
  onUpgradeComplete: () => void;
}

export default function UpgradeScreen({ currentPlanKey: initialPlanKey, onBack, onUpgradeComplete }: UpgradeScreenProps) {
  const [currentPlanKey, setCurrentPlanKey] = useState(initialPlanKey);
  const [loading, setLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const completeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => { if (completeTimerRef.current) clearTimeout(completeTimerRef.current); }, []);

  useEffect(() => {
    const controller = new AbortController();
    apiGet<{ status: string; planKey: string | null }>('/subscription/status')
      .then(({ planKey }) => { if (!controller.signal.aborted && planKey) setCurrentPlanKey(planKey); })
      .catch(() => {});
    return () => controller.abort();
  }, []);

  const handleSelect = async (plan: typeof PLANS[0]) => {
    if (plan.key === currentPlanKey) return;
    setLoading(plan.key);
    setError(null);
    try {
      if (currentPlanKey === 'free') {
        // Free users have no Stripe subscription — redirect to checkout
        const { url } = await apiPost<{ url: string }>('/checkout/create-session', {
          priceId: plan.priceId,
          planKey: plan.key,
        });
        window.location.href = url;
        return;
      }
      await upgradeSubscription(plan.priceId, plan.key);
      setCurrentPlanKey(plan.key);
      setSuccess(true);
      completeTimerRef.current = setTimeout(() => onUpgradeComplete(), 2000);
    } catch (err: any) {
      console.error('Upgrade error:', err);
      setError('Não foi possível processar o upgrade. Tente novamente.');
    } finally {
      setLoading(null);
    }
  };

  return (
    <div className="upgrade-overlay">
      <div
        className="upgrade-screen"
        role="dialog"
        aria-modal="true"
        aria-labelledby="upgrade-dialog-title"
      >
        {/* Header */}
        <div className="upgrade-header">
          <button className="upgrade-back-btn" onClick={onBack}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M19 12H5M12 5l-7 7 7 7" />
            </svg>
            Voltar
          </button>
          <div className="upgrade-launch-badge" aria-hidden="true">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
            </svg>
            50% Desconto de Lançamento
          </div>
        </div>

        {/* Hero */}
        <div className="upgrade-hero">
          <h1 id="upgrade-dialog-title">Faça Upgrade do Plano</h1>
          <p>{currentPlanKey === 'free' ? 'Você utilizou seu caso gratuito. Assine um plano para continuar praticando.' : 'Você atingiu o limite mensal de casos. Faça upgrade para continuar praticando agora.'}</p>
          <div className="upgrade-early-badge"><span aria-hidden="true">🚀</span> Early testers têm 50% off — por tempo limitado</div>
        </div>

        {success && (
          <div className="upgrade-success">
            ✅ Plano atualizado com sucesso! Redirecionando...
          </div>
        )}

        {error && (
          <div className="upgrade-error">{error}</div>
        )}

        {/* Payment notice */}
        <div className="upgrade-payment-notice">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
            <line x1="1" y1="10" x2="23" y2="10" />
          </svg>
          {currentPlanKey === 'free'
            ? 'Você será redirecionado para o checkout seguro do Stripe.'
            : 'O upgrade será cobrado no último método de pagamento utilizado.'}
        </div>

        {/* Plans */}
        <div className="upgrade-plans">
          {PLANS.map(plan => {
            const isCurrent = plan.key === currentPlanKey;
            const isLoading = loading === plan.key;
            return (
              <div
                key={plan.key}
                className={`upgrade-plan-card ${plan.popular ? 'upgrade-plan-featured' : ''} ${isCurrent ? 'upgrade-plan-current' : ''}`}
              >
                {plan.popular && <div className="upgrade-plan-badge">Mais Popular</div>}
                {isCurrent && <div className="upgrade-plan-badge upgrade-plan-badge-current">Plano Atual</div>}

                <div className="upgrade-plan-name">{plan.label}</div>
                <div className="upgrade-plan-price-row">
                  <span className="upgrade-plan-original">{plan.originalPrice}</span>
                  <span className="upgrade-plan-amount">{plan.price}</span>
                  <span className="upgrade-plan-period">/mês</span>
                </div>
                <div className="upgrade-plan-cases">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  {plan.cases}
                </div>

                <button
                  className={`upgrade-plan-btn ${plan.popular && !isCurrent ? 'upgrade-plan-btn-primary' : 'upgrade-plan-btn-outline'}`}
                  disabled={isCurrent || isLoading || success}
                  onClick={() => handleSelect(plan)}
                >
                  {isLoading ? 'Processando...' : isCurrent ? 'Plano Atual' : 'Fazer Upgrade'}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
